/**
 * Rows, groups and bar geometry for the robot lanes. Pure.
 *
 * ROWS. One row per robot, in the groups the site chose (or the default
 * groups by robot type, `robot-groups.ts`). Work with no robot yet -- a
 * scheduled occurrence RMF will assign when it fires, a queued task no
 * robot has bid on -- goes in an "Any robot" row, by `anyRobotGroupFor`
 * (labelled for the kind of robot its work needs, `anyRowLabel`):
 *
 *   1. it pins a FLEET: the first group holding a robot of that fleet;
 *   2. else the first group holding a robot whose type does this kind of
 *      work (a clean of any mode or a routine check -> cleaning, a
 *      delivery -> delivery, a patrol -> patrol);
 *   3. else a site-wide "Any robot" row above every group.
 *
 * An "Any robot" row is drawn only when it has something in it.
 *
 * BARS. `packRow` lays a row's items along a time axis. A bar is never
 * narrower than `MIN_BAR_PX`, so a four-minute delivery is still a thing
 * a finger or a pointer can hit; its true length is in the tooltip and
 * the side panel. Bars that would touch are stacked into sub-rows (the
 * row grows), never drawn over each other, so nothing is ever hidden.
 */
import type { CalendarLane, LanePaint } from '../calendar-categories';
import { DIDNT_FINISH, paintOf, type LaneItem } from './lane-items';
import type { TimeWindow } from './lane-time';

export type RobotService = 'cleaning' | 'delivery' | 'linen' | 'patrol' | 'other';

export const SERVICE_LABEL: Record<RobotService, string> = {
  cleaning: 'Cleaning',
  delivery: 'Delivery',
  linen: 'Linen',
  patrol: 'Patrol',
  other: 'Other',
};

export const SERVICE_ORDER: readonly RobotService[] = [
  'cleaning',
  'delivery',
  'linen',
  'patrol',
  'other',
];

export interface RobotEntry {
  /** `<fleet>/<robot>`. */
  key: string;
  fleet: string;
  name: string;
  /** What the row shows; the name unless the site relabelled it. */
  label: string;
  service: RobotService;
  /** The floor it was last seen on, when known ("L4"). */
  level?: string;
}

export interface GroupDef {
  id: string;
  name: string;
  /** Robot keys, in the order the rows are drawn. */
  robots: readonly string[];
}

/** The type of work a task lane belongs to, for the Any-robot rule. */
export function serviceOfLane(lane: CalendarLane): RobotService | null {
  switch (lane) {
    case 'vacuum':
    case 'sweep':
    case 'dust_mop':
    case 'scrub':
    case 'dustbag':
      return 'cleaning';
    case 'delivery':
      return 'delivery';
    case 'patrol':
      return 'patrol';
    default:
      return null;
  }
}

/**
 * WORK WITH NO ROBOT, ONE RULE (Akash, 2026-10-31), on the phone and the
 * laptop alike -- lanes, Agenda, schedule panels, phone sheets, task
 * drawers: a run no robot is pinned to names the kind of robot that will
 * take it ("Any cleaning robot"), "Any robot" only when its work names
 * no kind; a task the fleet has not given a robot yet is "Not assigned
 * yet". Never "RMF picks": hotel staff read these.
 */
const ANY_ROBOT_OF: Partial<Record<RobotService, string>> = {
  cleaning: 'Any cleaning robot',
  delivery: 'Any delivery robot',
  patrol: 'Any patrol robot',
};
export const ANY_ROBOT = 'Any robot';

/** "Any cleaning robot" for a clean of any mode or a routine check, and
 *  so on; "Any robot" for work of no kind a robot type does. */
export function anyRobotText(lane: CalendarLane): string {
  const service = serviceOfLane(lane);
  return (service && ANY_ROBOT_OF[service]) || ANY_ROBOT;
}

/** An "Any robot" row's label: the kind of robot all of its work needs,
 *  else "Any robot" (a row holding a clean and a delivery). */
export function anyRowLabel(items: readonly Pick<LaneItem, 'lane'>[]): string {
  const labels = new Set(items.map((it) => anyRobotText(it.lane)));
  return labels.size === 1 ? [...labels][0]! : ANY_ROBOT;
}

/** A task with no robot: "Not assigned yet" while it waits for one, and
 *  "Not assigned" once it has ended without one (a "yet" there promises
 *  a robot that will never come). */
export function unassignedText(ended: boolean): string {
  return ended ? 'Not assigned' : 'Not assigned yet';
}

export interface LaneRow {
  id: string;
  kind: 'robot' | 'any';
  robot?: RobotEntry;
  label: string;
  items: LaneItem[];
}

export interface LaneGroup {
  id: string;
  name: string;
  rows: LaneRow[];
  /** Robots in the group (not counting its Any-robot row). */
  robotCount: number;
  taskCount: number;
  didntFinish: number;
}

export interface LaneBoard {
  /** Site-wide "Any robot" row, or null when empty. */
  topAny: LaneRow | null;
  groups: LaneGroup[];
  /** Robots shown (after the filter). */
  robotCount: number;
}

/** Which group's "Any robot" row an unassigned item belongs in, or null
 *  for the site-wide one. See the module comment. */
export function anyRobotGroupFor(
  item: Pick<LaneItem, 'fleet' | 'lane'>,
  groups: readonly GroupDef[],
  robots: ReadonlyMap<string, RobotEntry>,
): string | null {
  if (item.fleet) {
    const byFleet = groups.find((g) => g.robots.some((k) => robots.get(k)?.fleet === item.fleet));
    if (byFleet) return byFleet.id;
  }
  const service = serviceOfLane(item.lane);
  if (service) {
    const byService = groups.find((g) => g.robots.some((k) => robots.get(k)?.service === service));
    if (byService) return byService.id;
  }
  return null;
}

/** True when `robot` matches the "Find robot" text: name, label, fleet
 *  or floor, case-insensitively. */
export function robotMatches(robot: RobotEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [robot.name, robot.label, robot.fleet, robot.level ?? ''].some((s) =>
    s.toLowerCase().includes(q),
  );
}

const UNGROUPED_ID = '__ungrouped';

/**
 * The rows the lanes draw, grouped. Items whose robot no group lists (a
 * robot retired from the groups, or one only the history knows) get a
 * trailing "Other robots" group rather than disappearing.
 *
 * With a filter, only matching robots are drawn and the Any-robot rows
 * are left out: "Find robot" answers "what is THIS robot doing", and an
 * unassigned task is not that.
 */
export function buildBoard(
  items: readonly LaneItem[],
  groupDefs: readonly GroupDef[],
  robots: readonly RobotEntry[],
  filter = '',
): LaneBoard {
  const byKey = new Map(robots.map((r) => [r.key, r]));
  const grouped = new Set(groupDefs.flatMap((g) => g.robots));
  const perRobot = new Map<string, LaneItem[]>();
  const perAny = new Map<string | null, LaneItem[]>();
  const extra: RobotEntry[] = [];

  for (const it of items) {
    if (!it.robotKey) {
      const gid = anyRobotGroupFor(it, groupDefs, byKey);
      const list = perAny.get(gid) ?? [];
      list.push(it);
      perAny.set(gid, list);
      continue;
    }
    if (!grouped.has(it.robotKey) && !extra.some((r) => r.key === it.robotKey)) {
      const known = byKey.get(it.robotKey);
      const name = it.robotName ?? it.robotKey.slice(it.robotKey.indexOf('/') + 1);
      extra.push(
        known ?? { key: it.robotKey, fleet: it.fleet ?? '', name, label: name, service: 'other' },
      );
    }
    const list = perRobot.get(it.robotKey) ?? [];
    list.push(it);
    perRobot.set(it.robotKey, list);
  }

  // Robots the site knows but no group lists still get a row.
  for (const r of robots) {
    if (!grouped.has(r.key) && !extra.some((e) => e.key === r.key)) extra.push(r);
  }

  const defs: GroupDef[] = [...groupDefs];
  if (extra.length > 0) {
    defs.push({
      id: UNGROUPED_ID,
      name: groupDefs.length ? 'Other robots' : 'Robots',
      robots: extra.map((r) => r.key),
    });
    for (const r of extra) if (!byKey.has(r.key)) byKey.set(r.key, r);
  }

  const filtering = filter.trim() !== '';
  let robotCount = 0;
  const groups: LaneGroup[] = [];
  for (const g of defs) {
    const rows: LaneRow[] = [];
    const anyItems = perAny.get(g.id) ?? [];
    if (!filtering && anyItems.length > 0) {
      rows.push({ id: `any:${g.id}`, kind: 'any', label: anyRowLabel(anyItems), items: anyItems });
    }
    let groupRobots = 0;
    for (const key of g.robots) {
      const robot = byKey.get(key);
      if (!robot) continue;
      if (filtering && !robotMatches(robot, filter)) continue;
      groupRobots += 1;
      rows.push({
        id: `robot:${key}`,
        kind: 'robot',
        robot,
        label: robot.label,
        items: perRobot.get(key) ?? [],
      });
    }
    if (filtering && groupRobots === 0) continue;
    if (rows.length === 0) continue;
    robotCount += groupRobots;
    const all = rows.flatMap((r) => r.items);
    groups.push({
      id: g.id,
      name: g.name,
      rows,
      robotCount: groupRobots,
      taskCount: all.length,
      didntFinish: all.filter((it) => DIDNT_FINISH.has(it.outcome)).length,
    });
  }

  const topItems = perAny.get(null) ?? [];
  return {
    topAny:
      !filtering && topItems.length > 0
        ? { id: 'any:__site', kind: 'any', label: anyRowLabel(topItems), items: topItems }
        : null,
    groups,
    robotCount,
  };
}

// ---- bar geometry ------------------------------------------------------

/** The narrowest a bar is ever drawn, px: a 24 px target (WCAG 2.5.8)
 *  plus its border. */
export const MIN_BAR_PX = 26;
/** Space kept between two bars in one sub-row, px. */
export const BAR_GAP_PX = 2;

export interface Axis extends TimeWindow {
  pxPerMs: number;
}

/** What `packRow` needs of a thing to lay it out. */
export interface Packable {
  id: string;
  startMs: number;
  endMs: number;
}

export interface Placement<T extends Packable = LaneItem> {
  item: T;
  /** Left edge from the axis origin, px. */
  x: number;
  /** Drawn width, px -- at least `MIN_BAR_PX`. */
  w: number;
  /** What the item's real length would draw at, px. */
  trueW: number;
  /** Which sub-row, from 0. */
  sub: number;
  /** The item started before the axis / ends after it. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

export interface PackedRow<T extends Packable = LaneItem> {
  placements: Placement<T>[];
  subRows: number;
}

function byStart(a: Packable, b: Packable): number {
  return a.startMs - b.startMs || a.endMs - b.endMs || a.id.localeCompare(b.id);
}

/**
 * Lays `items` along `axis`. Items wholly outside the axis are dropped;
 * one that crosses an edge is clipped to it and flagged, so the bar can
 * say it continues (an overnight clean on the morning after). A bar that
 * would reach past the right edge only because of its minimum width is
 * pulled back inside.
 */
export function packRow<T extends Packable>(
  items: readonly T[],
  axis: Axis,
  minWidthPx = MIN_BAR_PX,
  gapPx = BAR_GAP_PX,
): PackedRow<T> {
  const span = (axis.toMs - axis.fromMs) * axis.pxPerMs;
  const ends: number[] = [];
  const placements: Placement<T>[] = [];
  for (const item of [...items].sort(byStart)) {
    const zeroLength = item.endMs <= item.startMs;
    const inside = zeroLength
      ? item.startMs >= axis.fromMs && item.startMs < axis.toMs
      : item.startMs < axis.toMs && item.endMs > axis.fromMs;
    if (!inside) continue;
    const from = Math.max(item.startMs, axis.fromMs);
    const to = Math.min(item.endMs, axis.toMs);
    const trueW = Math.max(0, (to - from) * axis.pxPerMs);
    const w = Math.min(Math.max(trueW, minWidthPx), Math.max(minWidthPx, span));
    let x = (from - axis.fromMs) * axis.pxPerMs;
    if (x + w > span) x = Math.max(0, span - w);
    let sub = ends.findIndex((end) => end + gapPx <= x);
    if (sub === -1) {
      sub = ends.length;
      ends.push(0);
    }
    ends[sub] = x + w;
    placements.push({
      item,
      x,
      w,
      trueW,
      sub,
      clippedStart: item.startMs < axis.fromMs,
      clippedEnd: item.endMs > axis.toMs,
    });
  }
  return { placements, subRows: Math.max(1, ends.length) };
}

// ---- compact rows: one line per robot ----------------------------------

/** The sizes a density draws at, px. */
export interface LaneMetrics {
  /** The robot-name column. */
  nameW: number;
  rowMinH: number;
  barH: number;
  /** From one stacked bar's top to the next one's. */
  barStep: number;
  barTop: number;
  siteMinH: number;
}

export const COMFY_METRICS: LaneMetrics = {
  nameW: 150,
  rowMinH: 28,
  barH: 20,
  barStep: 22,
  barTop: 4,
  siteMinH: 30,
};

export const COMPACT_METRICS: LaneMetrics = {
  nameW: 104,
  rowMinH: 24,
  barH: 18,
  barStep: 20,
  barTop: 3,
  siteMinH: 26,
};


/** The narrowest a bar is drawn in compact rows, px. */
export const COMPACT_MIN_BAR_PX = 22;

/** One thing drawn on a lane: a single item, or several that were too
 *  close to draw apart (a count chip). */
export interface LaneChip<T extends Packable = LaneItem> extends Packable {
  items: T[];
}

/** `item` as a chip of its own. */
export function singleChip<T extends Packable>(item: T): LaneChip<T> {
  return { id: item.id, startMs: item.startMs, endMs: item.endMs, items: [item] };
}

/**
 * Lays `items` on ONE line (compact rows). Each is placed as `packRow`
 * would place it (clipped to the axis, at least `minWidthPx` wide); any
 * whose drawn bar would touch the one before it (closer than `gapPx`)
 * are merged with it into one chip, which reads as a count ("3") and
 * lists what it holds. Nothing is hidden: every item is in exactly one
 * chip. A lone item's chip has the item's own id; a merged one is
 * `cluster:<first item's id>`.
 */
export function mergeRow<T extends Packable>(
  items: readonly T[],
  axis: Axis,
  minWidthPx = COMPACT_MIN_BAR_PX,
  gapPx = BAR_GAP_PX,
): PackedRow<LaneChip<T>> {
  const single = packRow(items, axis, minWidthPx, gapPx).placements.sort(
    (a, b) => a.x - b.x || byStart(a.item, b.item),
  );
  const placements: Placement<LaneChip<T>>[] = [];
  let group: Placement<T>[] = [];
  let groupEnd = -Infinity;
  const flush = () => {
    if (group.length === 0) return;
    const first = group[0]!;
    if (group.length === 1) {
      placements.push({ ...first, item: singleChip(first.item), sub: 0 });
    } else {
      const list = group.map((p) => p.item);
      const x = first.x;
      placements.push({
        item: {
          id: `cluster:${first.item.id}`,
          startMs: Math.min(...list.map((i) => i.startMs)),
          endMs: Math.max(...list.map((i) => i.endMs)),
          items: list,
        },
        x,
        w: Math.max(groupEnd - x, minWidthPx),
        trueW: groupEnd - x,
        sub: 0,
        clippedStart: group.some((p) => p.clippedStart),
        clippedEnd: group.some((p) => p.clippedEnd),
      });
    }
    group = [];
    groupEnd = -Infinity;
  };
  for (const p of single) {
    if (group.length > 0 && p.x >= groupEnd + gapPx) flush();
    group.push(p);
    groupEnd = Math.max(groupEnd, p.x + p.w);
  }
  flush();
  return { placements, subRows: 1 };
}

// ---- week cells --------------------------------------------------------

export interface WeekBlock {
  id: string;
  /** Percent of the cell's strip. */
  left: number;
  width: number;
  /** What the block is drawn as (`paintOf`). */
  lane: LanePaint;
  outcome: LaneItem['outcome'];
}

export interface WeekCell {
  count: number;
  didntFinish: number;
  blocks: WeekBlock[];
}

/** The narrowest a week-strip block is drawn, percent of the strip. */
export const MIN_WEEK_BLOCK_PCT = 0.8;

/**
 * One robot's day as a week cell: every item that day counts, and each
 * is drawn on the `strip` (the working hours, or the whole day), clamped
 * to its edges so night work still shows as a mark at the end.
 */
export function weekCell(items: readonly LaneItem[], day: TimeWindow, strip: TimeWindow): WeekCell {
  const span = strip.toMs - strip.fromMs;
  const inDay = items.filter((it) =>
    it.endMs <= it.startMs
      ? it.startMs >= day.fromMs && it.startMs < day.toMs
      : it.startMs < day.toMs && it.endMs > day.fromMs,
  );
  const blocks: WeekBlock[] = [...inDay].sort(byStart).map((it) => {
    const from = Math.min(Math.max(it.startMs, strip.fromMs), strip.toMs);
    const to = Math.min(Math.max(it.endMs, strip.fromMs), strip.toMs);
    const width = Math.max(MIN_WEEK_BLOCK_PCT, ((to - from) / span) * 100);
    const left = Math.min(((from - strip.fromMs) / span) * 100, 100 - width);
    return { id: it.id, left, width, lane: paintOf(it), outcome: it.outcome };
  });
  return {
    count: inDay.length,
    didntFinish: inDay.filter((it) => DIDNT_FINISH.has(it.outcome)).length,
    blocks,
  };
}
